import React, { useContext } from 'react';
import EDListItem, {
  EDListItemButton,
} from '../../../../../../../../../BaseComponents/List/Components/EDListItem';
import EDIcon from '../../../../../../../../../BaseComponents/Icon/EDIcon';
import EDListItemText from '../../../../../../../../../BaseComponents/List/Components/EDListItemText';
import EDCollapse from '../../../../../../../../../BaseComponents/Collapsables/Collapse/EDCollapse';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import { IconType } from '../../../../../../../../../BaseComponents/Icon/Utils/IconDefinitions';
import classNames from 'classnames';
import { sideMenuListItemGetScrollTop } from './Utils/EkonomiskAnalysExpandableListItemUtils';
import { EkonomiskAnalysSideMenuContext } from '../../Context/EkonomiskAnalysSideMenuContext';
import ChangedMarker from '../../../../../../../../../Components/ChangedMarker/ChangedMarker';
import EkonomiskAnalysSideMenuIconButton from '../IconButton/EkonomiskAnalysSideMenuIconButton';

interface IProps {
  text: string;
  iconType: IconType;
  sideMenuOpen: boolean;
  openSideMenu: () => void;
  open: boolean;
  toggleOpen: () => void;
  changed?: boolean;
  unmountOnExit?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  classes?: any;
  children?: React.ReactNode;
}

interface IInnerProps extends IProps {
  scrollContainerEl: HTMLDivElement | null;
}

const styles = (theme: Theme) => ({
  listItem: {
    marginTop: 1,
    paddingRight: '12px',
    paddingTop: 0,
    paddingBottom: 0,
    outline: 0,
  },
  listItemLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  listItemSideMenuOpen: {
    marginTop: 0,
    backgroundColor:
      theme.customProperties.EASideMenuColors.selectedOptionsSectionBGColor,
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: '11px',
    paddingBottom: '11px',
    paddingLeft: 24,
  },
  listItemOpen: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  collapseSideMenuOpen: {
    '&:last-child': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  iconButton: {
    margin: '0px 0px 0px -8px',
  },
});

/**
 * En utfällbar titel som används för att utgöra dem olika avsnitten i sidomenyn
 */
export class EkonomiskAnalysExpandableListItem extends React.Component<IInnerProps> {
  listItemRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  animationFrameID: number | undefined;

  constructor(props: IInnerProps) {
    super(props);
    this.listItemRef = React.createRef();
    this.contentRef = React.createRef();
  }

  contentOnEntering = () => {
    const { scrollContainerEl } = this.props;
    if (
      this.listItemRef.current &&
      scrollContainerEl &&
      this.contentRef.current
    ) {
      const scrollTop = sideMenuListItemGetScrollTop(
        this.listItemRef.current,
        this.contentRef.current,
        scrollContainerEl
      );
      if (scrollTop !== -1) {
        const animationFrameCallback = () => {
          scrollContainerEl.scrollTop = scrollTop;
          this.animationFrameID = requestAnimationFrame(animationFrameCallback);
        };
        this.animationFrameID = requestAnimationFrame(animationFrameCallback);
      }
    }
  };

  contentOnEntered = () => {
    if (this.animationFrameID) {
      cancelAnimationFrame(this.animationFrameID);
    }
  };

  contentOnExited = () => {
    if (this.animationFrameID) {
      cancelAnimationFrame(this.animationFrameID);
    }
  };

  onClick = () => {
    if (!this.props.sideMenuOpen) {
      this.props.openSideMenu();
    } else {
      this.props.toggleOpen();
    }
  };

  render() {
    const { sideMenuOpen, open, classes, text, iconType } = this.props;
    const itemOpen = sideMenuOpen && open;

    return (
      <>
        {sideMenuOpen ? (
          <EDListItemButton
            ref={this.listItemRef}
            onClick={sideMenuOpen ? this.onClick : undefined}
            className={classNames(classes.listItem, {
              [classes.listItemSideMenuOpen]: sideMenuOpen,
              [classes.listItemOpen]: itemOpen,
            })}
            disabled={this.props.disabled}
            disabledReason={this.props.disabledReason}
          >
            <EkonomiskAnalysSideMenuIconButton
              sideMenuOpen={sideMenuOpen}
              tooltipText={text}
              iconType={iconType}
              onClick={this.onClick}
              disabled={this.props.disabled}
              disabledReason={this.props.disabledReason}
            />
            <EDListItemText
              primary={
                <div className={this.props.classes.listItemLabel}>
                  {text}
                  {this.props.changed && <ChangedMarker leftMargin />}
                </div>
              }
              bolder
            />
            {itemOpen ? (
              <EDIcon iconType="expand_less" />
            ) : (
              <EDIcon iconType="expand_more" />
            )}
          </EDListItemButton>
        ) : (
          <EDListItem
            className={classNames(classes.listItem, {
              [classes.listItemSideMenuOpen]: sideMenuOpen,
              [classes.listItemOpen]: itemOpen,
            })}
            disabled={this.props.disabled}
            disabledReason={this.props.disabledReason}
          >
            <EkonomiskAnalysSideMenuIconButton
              sideMenuOpen={sideMenuOpen}
              tooltipText={text}
              iconType={iconType}
              onClick={this.onClick}
              disabled={this.props.disabled}
              disabledReason={this.props.disabledReason}
            />
            <EDListItemText
              primary={
                <div className={this.props.classes.listItemLabel}>
                  {text}
                  {this.props.changed && <ChangedMarker leftMargin />}
                </div>
              }
              bolder
            />
            {itemOpen ? (
              <EDIcon iconType="expand_less" />
            ) : (
              <EDIcon iconType="expand_more" />
            )}
          </EDListItem>
        )}
        <EDCollapse
          open={itemOpen}
          timeout="auto"
          className={classNames({
            [classes.collapseSideMenuOpen]: sideMenuOpen,
          })}
          onEntering={this.contentOnEntering}
          onEntered={this.contentOnEntered}
          onExited={this.contentOnExited}
          unmountOnExit={this.props.unmountOnExit}
        >
          <div ref={this.contentRef}>{this.props.children}</div>
        </EDCollapse>
      </>
    );
  }
}

const EnhancedEkonomiskAnalysExpandableListItem: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { scrollContainerRef } = useContext(EkonomiskAnalysSideMenuContext);
  return (
    <EkonomiskAnalysExpandableListItem
      scrollContainerEl={scrollContainerRef.current}
      {...props}
    />
  );
};

export default withStyles(EnhancedEkonomiskAnalysExpandableListItem, styles);
